<script lang="ts" setup>
import { type PermissionKey, type WorkspaceUserRoles, getEffectiveBaseRole } from 'nocodb-sdk'
import { PermissionMeta, PermissionRoleMap, PermissionRolePower, ProjectRoles, WorkspaceRolesToProjectRoles } from 'nocodb-sdk'
import type { NcListProps } from '#imports'

interface Props extends Partial<NcListProps> {
  selectedUsers: Set<string>
  baseId: string
  permissionLabel: string
  permissionDescription?: string
  permission?: PermissionKey
  readonly?: boolean
  listClassName?: string
  showListFooter?: boolean
  disabledUsers?: string[]
  /** Override minimum role — uses the more restrictive of this and PermissionMeta.minimumRole */
  minimumRoleOverride?: string
  teamHierarchyScopes?: Record<string, 'self_only' | 'self_and_descendants'>
}

const props = withDefaults(defineProps<Props>(), {})

const emits = defineEmits(['update:selectedUsers', 'change', 'update:open', 'escape', 'toggleTeamScope'])

const selectedUsers = useVModel(props, 'selectedUsers', emits)

const vOpen = useVModel(props, 'open', emits)

const { t } = useI18n()

// Destructure known props so restProps only contains NcList pass-through props
const {
  open: _open,
  selectedUsers: _selectedUsers,
  baseId,
  permissionLabel: _permissionLabel,
  permissionDescription: _permissionDescription,
  permission,
  readonly: _readonly,
  listClassName,
  showListFooter,
  disabledUsers: _disabledUsers,
  minimumRoleOverride: _minimumRoleOverride,
  teamHierarchyScopes: _teamHierarchyScopes,
  ...restProps
} = props

const { blockTeamsManagement } = useEeConfig()

const { isTeamsEnabled } = storeToRefs(useWorkspace())

const basesStore = useBases()

const { basesUser, basesTeams } = storeToRefs(basesStore)

const baseStore = useBase()

const { base } = storeToRefs(baseStore)

const listRef = ref()

// Todo: @rameshmane7218, @pranav - remove this flag once the teams table and field permissions are enabled
const isTeamsTableAndFieldPermissionsEnabled = true

const showTeams = computed(() => {
  return !blockTeamsManagement.value && isTeamsEnabled.value && isTeamsTableAndFieldPermissionsEnabled
})

const baseUsers = computed(() => {
  return (basesUser.value.get(baseId) || []).map((user) => ({
    ...user,
    roles: getEffectiveBaseRole({
      baseRole: user.roles as ProjectRoles,
      baseTeamRole: (user as any).base_team_roles as ProjectRoles,
      workspaceRole: user.workspace_roles as WorkspaceUserRoles,
      workspaceTeamRole: (user as any).workspace_team_roles as WorkspaceUserRoles,
      defaultBaseRole: base.value?.default_role,
    }),
    ncGroupHeaderLabel: showTeams.value ? t('labels.members') : undefined,
  }))
})

const baseTeams = computed<NcListItemType[]>(() => {
  if (!showTeams.value) return []

  return (basesTeams.value.get(baseId) || []).map((bt) => ({
    ...bt,
    id: bt.team_id,
    isTeam: true,
    display_name: bt.team_title,
    roles:
      bt.base_role ??
      (bt.workspace_role
        ? WorkspaceRolesToProjectRoles[bt.workspace_role as WorkspaceUserRoles] ?? ProjectRoles.NO_ACCESS
        : ProjectRoles.NO_ACCESS),
    base_roles: bt.base_role,
    ncGroupHeaderLabel: t('general.teams'),
  })) as NcListItemType[]
})

// Filter users based on minimum role requirement from PermissionMeta
const roleFilteredUsers = computed<NcListItemType[]>(() => {
  if (!permission) return baseTeams.value.concat(baseUsers.value)

  const permissionMeta = PermissionMeta[permission]
  if (!permissionMeta) return baseTeams.value.concat(baseUsers.value)

  const minimumRolePower = PermissionRolePower[permissionMeta.minimumRole]
  if (!minimumRolePower) return baseTeams.value.concat(baseUsers.value)

  return baseTeams.value.concat(baseUsers.value).filter((user) => {
    if (typeof user.roles === 'string') {
      const userRoles = (user.roles as string).split(',').map((r) => r.trim())
      return userRoles.some((role) => {
        const mappedRole = PermissionRoleMap[role as keyof typeof PermissionRoleMap]
        const rolePower = PermissionRolePower[mappedRole]
        return (rolePower && rolePower >= minimumRolePower) || selectedUsers.value.has(user.id)
      })
    }

    return Object.keys(user.roles ?? {}).some((role) => {
      const mappedRole = PermissionRoleMap[role as keyof typeof PermissionRoleMap]
      const rolePower = PermissionRolePower[mappedRole]
      return (rolePower && rolePower >= minimumRolePower) || selectedUsers.value.has(user.id)
    })
  })
})

// Users whose role is below the parent's effective permission — shown but disabled
const parentRestrictedUserIds = computed<string[]>(() => {
  if (!props.minimumRoleOverride) return []

  const overridePower = PermissionRolePower[props.minimumRoleOverride as keyof typeof PermissionRolePower]
  if (!overridePower) return []

  return roleFilteredUsers.value
    .filter((user) => {
      if (typeof user.roles === 'string') {
        const userRoles = (user.roles as string).split(',').map((r) => r.trim())
        return !userRoles.some((role) => {
          const mappedRole = PermissionRoleMap[role as keyof typeof PermissionRoleMap]
          const rolePower = PermissionRolePower[mappedRole]
          return rolePower && rolePower >= overridePower
        })
      }
      return !Object.keys(user.roles ?? {}).some((role) => {
        const mappedRole = PermissionRoleMap[role as keyof typeof PermissionRoleMap]
        const rolePower = PermissionRolePower[mappedRole]
        return rolePower && rolePower >= overridePower
      })
    })
    .map((user) => user.id)
})

// Merge externally disabled users with parent-restricted users
const allDisabledUsers = computed<string[]>(() => {
  return [...(props.disabledUsers || []), ...parentRestrictedUserIds.value]
})

const computedNcList = computed(() => {
  return listRef.value?.list ?? roleFilteredUsers.value
})

// Selected users display
const selectedUsersList = computed(() => {
  return Array.from(selectedUsers.value)
    .map((userId) => {
      // Check if it's a team
      const team = baseTeams.value.find((team) => team.id === userId)
      if (team) {
        return team
      }
      // It's a user
      return baseUsers.value.find((user) => user.id === userId)
    })
    .filter(Boolean)
})

const toggleUser = (userId: string) => {
  if (props.readonly) return

  if (selectedUsers.value.has(userId)) {
    selectedUsers.value.delete(userId)
  } else {
    selectedUsers.value.add(userId)
  }

  emits('change', selectedUsers.value)
}

const selectAll = (filteredList: NcListItemType[] = listRef?.value?.list ?? roleFilteredUsers.value) => {
  if (props.readonly) return

  filteredList.forEach((user) => {
    if (!allDisabledUsers.value.includes(user.id)) {
      selectedUsers.value.add(user.id)
    }
  })

  emits('change', selectedUsers.value)
}

const clearAll = (filteredList: NcListItemType[] = listRef?.value?.list ?? roleFilteredUsers.value) => {
  if (props.readonly) return

  filteredList.forEach((user) => {
    selectedUsers.value.delete(user.id)
  })

  emits('change', selectedUsers.value)
}

const selectedUserIds = computed(() => {
  return selectedUsersList.value.map((user) => user?.id) as string[]
})

const handleUpdateValue = (option: NcListItemType) => {
  if (allDisabledUsers.value.includes(option.id)) return
  toggleUser(option.id)
}

const filterOption = (input: string, option: NcListItemType) => {
  return antSelectFilterOption(input, option, ['email', 'display_name'])
}

defineExpose({
  selectAll,
  clearAll,
  list: computedNcList,
})
</script>

<template>
  <NcList
    ref="listRef"
    :open="vOpen"
    v-bind="restProps"
    :value="selectedUserIds"
    :list="roleFilteredUsers"
    :group-order="[$t('general.teams'), $t('labels.members')]"
    option-label-key="email"
    option-value-key="id"
    :item-height="52"
    :search-input-placeholder="$t('placeholder.searchMember')"
    is-multi-select
    :class="listClassName"
    :filter-option="filterOption"
    empty-description="No users found"
    @change="handleUpdateValue($event)"
    @escape="emits('escape', $event)"
  >
    <template #listItemExtraLeft="{ isSelected, option }">
      <NcCheckbox :checked="isSelected" :class="{ 'opacity-40': parentRestrictedUserIds.includes(option.id) }" />
    </template>

    <template #listItemContent="{ option }">
      <NcTooltip class="flex-1" :disabled="!parentRestrictedUserIds.includes(option.id)" placement="right">
        <template #title>
          {{ $t('msg.info.cannotBeMorePermissiveThanParent') }}
        </template>
        <div :class="{ 'opacity-40 cursor-not-allowed': parentRestrictedUserIds.includes(option.id) }">
          <GeneralTeamInfo
            v-if="option?.isTeam"
            :team="transformToTeamObject(option)"
            :disabled="allDisabledUsers.includes(option.id)"
            class="max-w-[calc(100%_-_24px)]"
          />
          <NcUserInfo
            v-else-if="option?.email"
            :user="option"
            :disabled="allDisabledUsers.includes(option.id)"
            class="w-full max-w-[calc(100%_-_24px)]"
          />
          <template v-else>{{ option.email ?? option.id }} </template>
        </div>
      </NcTooltip>
    </template>
    <template #listItemExtraRight="{ option, isSelected }">
      <div class="flex items-center gap-1" :class="{ 'opacity-40': parentRestrictedUserIds.includes(option.id) }">
        <div
          v-if="option?.isTeam && isSelected"
          class="nc-team-scope-toggle flex items-center rounded-md border-1 border-nc-border-gray-medium flex-none"
          @click.stop
        >
          <NcTooltip placement="top" class="flex">
            <template #title>{{ $t('tooltip.teamScopeThisOnlyDesc') }}</template>
            <div
              class="nc-team-scope-segment px-1.5 py-0.5 text-[10px] leading-tight font-medium transition-colors cursor-pointer rounded-l-[5px]"
              :class="
                teamHierarchyScopes?.[option.id] === 'self_only'
                  ? 'bg-nc-fill-primary text-white'
                  : 'text-nc-content-gray-subtle hover:bg-nc-bg-gray-light'
              "
              @click="teamHierarchyScopes?.[option.id] !== 'self_only' ? emits('toggleTeamScope', option.id) : undefined"
            >
              {{ $t('labels.thisTeamOnly') }}
            </div>
          </NcTooltip>
          <NcTooltip placement="top" class="flex">
            <template #title>{{ $t('tooltip.teamScopeWithSubTeamsDesc') }}</template>
            <div
              class="nc-team-scope-segment px-1.5 py-0.5 text-[10px] leading-tight font-medium transition-colors cursor-pointer rounded-r-[5px]"
              :class="
                teamHierarchyScopes?.[option.id] !== 'self_only'
                  ? 'bg-nc-fill-primary text-white'
                  : 'text-nc-content-gray-subtle hover:bg-nc-bg-gray-light'
              "
              @click="teamHierarchyScopes?.[option.id] === 'self_only' ? emits('toggleTeamScope', option.id) : undefined"
            >
              {{ $t('labels.withSubTeams') }}
            </div>
          </NcTooltip>
        </div>
        <RolesBadge :border="false" :role="option.roles" icon-only nc-badge-class="!px-1" show-tooltip>
          <template #tooltip="{ label }">
            {{ $t('tooltip.basePermissionRole', { role: $t(`objects.roleType.${label}`) }) }}
          </template>
        </RolesBadge>
      </div>
    </template>
    <template #listItemSelectedIcon> </template>
    <template v-if="showListFooter && roleFilteredUsers.length > 1" #listFooter>
      <NcDivider class="!my-0" />

      <div class="flex items-center p-2 gap-3">
        <NcButton type="text" size="small" :disabled="listRef?.list?.length === 0" @click="selectAll()"> Select all </NcButton>
        <NcButton type="text" size="small" :disabled="selectedUsers.size === 0" @click="clearAll()"> Clear all </NcButton>
      </div>
    </template>
  </NcList>
</template>
